<template>
    <NonSkuStoneModal
        :data-qa="dataQa"
        :is-open="!!stone"
        :is-added="
            ringBuilderStone?.__typename === 'NonSkuLabGrown' &&
            ringBuilderStone.id === stone?.id
        "
        :is-added-route="isAddedRoute"
        :shape="stone?.shape.systemCode"
        @close="close"
        @select="selectStone"
    >
        <template #title>
            {{ title }}
        </template>

        <template #default>
            <StoneDetails
                v-if="stone"
                :data-qa-stone-modal-props="
                    JSON.stringify({
                        shape: stone.shape.systemCode,
                    })
                "
                :image
            >
                <template #default>
                    <dl class="details">
                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.price') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/lab-grown-diamonds/price"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{ price }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_carat_weight') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/lab-grown-diamonds/carat-weight"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{ Number(stone.caratWeight).toFixed(2) }}ct
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_colour') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/lab-grown-diamonds/colour"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{
                                    stone.color.translation.name ||
                                    stone.color.systemCode
                                }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                {{ t('diamond_search.property_clarity') }}
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/lab-grown-diamonds/clarity"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">
                                {{
                                    stone.clarity.translation.name ||
                                    stone.clarity.systemCode
                                }}
                            </dd>
                        </div>

                        <div class="details-item">
                            <dt
                                class="title"
                                role="heading"
                                aria-level="4"
                            >
                                IGI report
                                <LazyTooltip
                                    inline
                                    path="/diamond-search/lab-grown-diamonds/igi-report"
                                    :page-type="pageType"
                                />
                            </dt>

                            <dd class="description">Provided post checkout</dd>
                        </div>
                    </dl>

                    <ShopWithConfidence
                        :ninety-day-returns
                        ethically-sourced-diamonds
                        price-match-guarantee
                        :page-type="pageType"
                    />
                </template>
            </StoneDetails>
        </template>
    </NonSkuStoneModal>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { NonSkuLabGrownFragment } from '@/graphql/api'
import { useStoneImage } from '@/domain/stone/useStoneImage'
import { useStonePrice } from '../../useStonePrice'

import NonSkuStoneModal from '@/components/stone/search/nonsku/modal/NonSkuStoneModal.vue'
import StoneDetails from '../../sku/StoneDetails.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import ShopWithConfidence from '../../ShopWithConfidence.vue'
import { useRoutes } from '@/router/routes.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    stone?: NonSkuLabGrownFragment
    dataQa?: string
    pageType: PageType
}>()

const emit = defineEmits<{
    close: []
    select: [stone: NonSkuLabGrownFragment]
}>()

const close = () => {
    emit('close')
}

const { t } = useI18n()

const stoneImage = useStoneImage(() => props.stone)
const image = computed(
    () =>
        stoneImage.value && {
            ...stoneImage.value,
            alt: `${props.stone?.shape.translation.name || props.stone?.shape.systemCode}`,
        }
)

const { price, totalSaleAmount } = useStonePrice(toRef(() => props.stone))

const ninetyDayReturns = computed(
    () => parseFloat(totalSaleAmount.value.amount) < 6000
)

const title = computed(() => {
    const st = props.stone

    if (!st) {
        return ''
    }

    return `${
        st.shape.translation.name || st.shape.systemCode
    } ${Number(st.caratWeight).toFixed(2)}ct`
})

const {
    addStone,
    stone: ringBuilderStone,
    ring: ringBuilderRing,
} = useRingBuilder()

const routes = useRoutes()

const isAddedRoute = computed(
    () =>
        ringBuilderRing.value &&
        routes.ringBuilder.ring(ringBuilderRing.value, ringBuilderStone.value)
)
const selectStone = async () => {
    const st = props.stone

    if (st) {
        await addStone(st)

        emit('select', st)
    }
}
</script>
